import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource } from '@angular/material/table';
import { DescriptionComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/description/description.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { IYoutubeStreamApi } from '@interfaces/campaign-analytics-api.interface';
import { IDescriptionAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';

@Component({
  imports: [TranslateModule, MatButtonModule, DescriptionComponent, LowerCasePipe],
  providers: [DeliverableFindingsStore],
  selector: 'app-description-stream-analytics',
  templateUrl: './description-stream-analytics.component.html',
})
export class DescriptionStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  { id: string; timestamp?: string | null },
  IDescriptionAnalytics
> {
  public override readonly deliverableType = EDeliverableType.description;
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'description-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.talkingPoint.totalCount'),
    value: 0,
  };

  public override readonly displayedColumns: string[] = [];
  public override readonly dataSource: MatTableDataSource<{ id: string; timestamp?: string | null }> =
    new MatTableDataSource();
  public readonly keywordList = signal<IKpiCard[]>([]);

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.descriptions?.find((c) => c.deliverableId === deliverableId);
    }

    return null;
  });

  public readonly allKpiCards = computed(() => [this.kpiCardData, ...this.keywordList()]);

  public readonly contentData = computed(() => {
    const socialContent = this.stream() as IYoutubeStreamApi;

    if (!socialContent) {
      return null;
    }

    if ('youtubeLiveData' in socialContent) {
      return socialContent.youtubeLiveData;
    }

    return null;
  });

  private keywordCounts: Record<string, number> = {};

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();
        this.setStatus();
        this.setStreamData();
        this.setKeywordList();
      }
    });

    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        const description = this.currentDeliverable();

        this.keywords = description?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }

  public override highlightKeywords(text: string): string {
    const normalized = AnalyticsDetailsUtilitiesService.normalizeWhitespace(text);

    return AnalyticsDetailsUtilitiesService.highlightKeywords(this.keywords, normalized);
  }

  protected override setStatus(): void {
    this.status.set(this.currentDeliverable()?.status ?? EAnalyticsStatus.processing);
  }

  protected override prepareChartAndTableData(): void {
    return;
  }

  protected override setCounts(): void {
    const description = this.currentDeliverable();

    const keywords = description?.keywords?.map((k) => k.keyword) ?? [];
    const descriptionText = AnalyticsDetailsUtilitiesService.normalizeWhitespace(this.contentData()?.description ?? '');
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, [descriptionText]);

    const total = Object.values(this.keywordCounts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  protected override setKeywordList(): void {
    const description = this.currentDeliverable();

    if (description?.keywords && description.keywords.length) {
      const kpiCardList = description.keywords.map((k) => {
        return {
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
          value: this.keywordCounts[k.keyword] ?? 0,
        };
      });

      this.keywordList.set(kpiCardList);
    }
  }
}
